import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Modal,
  Form,
  Select,
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Input,
  Typography,
  Tooltip,
  Badge
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BookOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  SearchOutlined
} from '@ant-design/icons';
import apiService from "services/api/apiService";
import { feedbackSuccess, feedbackError } from "../utils/feedback";
import TrainingProgramForm from '../components/Training/TrainingProgramForm';
import KirkpatrickEvaluation from '../components/Training/KirkpatrickEvaluation';

const { Title } = Typography;
const { Option } = Select;

const TrainingDevelopment: React.FC = () => {
  const { isEmployee } = useAuth();
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingProgram, setEditingProgram] = useState<any>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [kirkpatrickVisible, setKirkpatrickVisible] = useState(false);
  const [enrollModal, setEnrollModal] = useState(false);
  const [enrollForm] = Form.useForm();
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);

  useEffect(() => {
    fetchTrainingData();
    fetchEnrollments();
  }, []);

  useEffect(() => {
    apiService.getEmployees().then((r: any) => { if (r.success) setEmployees(r.data || []); });
  }, []);

  const fetchEnrollments = async () => {
    try {
      const r = await apiService.getTrainingEnrollments();
      if (r.success) setEnrollments(r.data || []);
    } catch {}
  };

  const fetchTrainingData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTrainingPrograms();
      console.log('Training programs response:', response);
      
      if (response.success) {
        // Ensure we always have an array
        let programData = response.data || [];
        
        // Handle different response formats
        if (!Array.isArray(programData)) {
          if (typeof programData === 'object' && programData.programs) {
            programData = programData.programs;
          } else if (typeof programData === 'object' && programData.data) {
            programData = programData.data;
          } else {
            programData = [];
          }
        }
        
        setPrograms(Array.isArray(programData) ? programData : []);
      } else {
        console.error('Failed to fetch training programs:', response.error);
        // Use fallback mock data
        setPrograms([
          {
            id: 1,
            program_id: 'PROG_001',
            title: 'React Development Fundamentals',
            description: 'Learn the basics of React development including components, state, and props.',
            category: 'technical',
            type: 'online',
            duration_hours: 40,
            max_participants: 25,
            cost_per_participant: 15000,
            trainer_name: 'John Doe',
            trainer_email: 'john@example.com',
            status: 'active',
            created_at: '2024-11-01'
          },
          {
            id: 2,
            program_id: 'PROG_002',
            title: 'Safety Compliance Training',
            description: 'Mandatory safety training for all employees.',
            category: 'safety',
            type: 'workshop',
            duration_hours: 8,
            max_participants: 50,
            cost_per_participant: 5000,
            trainer_name: 'Mike Johnson',
            trainer_email: 'mike@example.com',
            status: 'active',
            created_at: '2024-11-03'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching training data:', error);
      message.error('Failed to fetch training data');
      // Ensure programs is always an array even on error
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (program: any) => {
    setEditingProgram(program);
    setFormVisible(true);
  };

  const handleDelete = async (programId: string) => {
    try {
      const response = await apiService.deleteTrainingProgram(programId);
      if (response.success) {
        message.success('Training program deleted successfully');
        fetchTrainingData(); // Refresh the data
      } else {
        message.error(response.error || 'Delete failed');
      }
    } catch (error) {
      message.error('An error occurred while deleting');
    }
  };

  const handleFormSuccess = () => {
    setFormVisible(false);
    setEditingProgram(null);
    fetchTrainingData(); // Refresh the data after successful create/update
  };

  const handleFormCancel = () => {
    setFormVisible(false);
    setEditingProgram(null);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'active': 'success',
      'completed': 'default',
      'cancelled': 'error',
      'draft': 'warning'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'technical': 'blue',
      'soft_skills': 'green',
      'compliance': 'orange',
      'leadership': 'purple',
      'safety': 'red',
      'orientation': 'cyan'
    };
    return colors[category as keyof typeof colors] || 'default';
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      'online': '💻',
      'classroom': '🏫',
      'workshop': '🔧',
      'seminar': '📋',
      'certification': '🏆'
    };
    return icons[type as keyof typeof icons] || '📚';
  };

  // Ensure programs is always an array before filtering
  const safePrograms = Array.isArray(programs) ? programs : [];
  
  // Filter programs based on search and filters
  const filteredPrograms = safePrograms.filter(program => {
    const matchesSearch = program.title?.toLowerCase().includes(searchText.toLowerCase()) ||
                         program.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                         program.trainer_name?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = statusFilter === 'all' || program.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || program.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const programColumns = [
    {
      title: 'Program',
      key: 'program',
      render: (record: any) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {getTypeIcon(record.type)} {record.title}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: {record.program_id}
          </div>
          <Tag color={getCategoryColor(record.category)} style={{ marginTop: 4 }}>
            {record.category?.replace('_', ' ').toUpperCase()}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Details',
      key: 'details',
      render: (record: any) => (
        <div>
          <div><ClockCircleOutlined /> {record.duration_hours}h</div>
          <div><UserOutlined /> Max: {record.max_participants}</div>
          {record.cost_per_participant > 0 && (
            <div><DollarOutlined /> ₦{record.cost_per_participant?.toLocaleString()}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Trainer',
      dataIndex: 'trainer_name',
      key: 'trainer_name',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.trainer_email}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    ...(isEmployee() ? [] : [{
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Tooltip title="Edit Program">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this program?"
            onConfirm={() => handleDelete(record.id.toString())}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Program">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
            <Tooltip title="Enroll Employee">
                <Button type="text" size="small" icon={<UserOutlined />}
                  onClick={() => { setSelectedProgram(record); setEnrollModal(true); }} />
              </Tooltip>
            <Tooltip title="Kirkpatrick Evaluation">
              <Button
                type="text"
                size="small"
                style={{ color: '#722ed1' }}
                onClick={() => { setSelectedProgram(record); setKirkpatrickVisible(true); }}
              >
                📊 Evaluate
              </Button>
            </Tooltip>
        </Space>
      ),
    }]),
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <BookOutlined /> Training & Development
        </Title>
      </div>

      {/* Dashboard Statistics */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Programs"
              value={safePrograms.length}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Programs"
              value={safePrograms.filter(p => p.status === 'active').length}
              prefix={<Badge status="success" />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Participants"
              value={safePrograms.reduce((sum, p) => sum + (p.max_participants || 0), 0)}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Training Hours"
              value={safePrograms.reduce((sum, p) => sum + parseFloat(String(p.duration_hours || 0)), 0)}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Search programs, trainers, or descriptions..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 120 }}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="draft">Draft</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: 140 }}
            >
              <Option value="all">All Categories</Option>
              <Option value="technical">Technical</Option>
              <Option value="soft_skills">Soft Skills</Option>
              <Option value="compliance">Compliance</Option>
              <Option value="leadership">Leadership</Option>
              <Option value="safety">Safety</Option>
              <Option value="orientation">Orientation</Option>
            </Select>
          </Col>
          {!isEmployee() && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setFormVisible(true)}
            >
              Add Program
            </Button>
          </Col>
          )}
        </Row>
      </Card>

      {/* Training Programs Table */}
      <Card title="Training Programs">
        <Table
          columns={programColumns}
          dataSource={filteredPrograms}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} programs`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Training Program Form Modal */}
      <TrainingProgramForm
        visible={formVisible}
        onCancel={handleFormCancel}
        onSuccess={handleFormSuccess}
        editingProgram={editingProgram}
      />
      {/* Enroll Employee Modal */}
      <Modal title={`Enroll Employee — ${selectedProgram?.title || ''}`}
        open={enrollModal} onCancel={() => { setEnrollModal(false); enrollForm.resetFields(); }} footer={null} width={480}>
        <Form form={enrollForm} layout="vertical" onFinish={async (values) => {
          const r = await apiService.enrollInBenefit({ ...values, program_id: selectedProgram?.program_id, tenant_id: apiService.getTenantId() });
          if (r && r.success) { message.success('Employee enrolled successfully'); setEnrollModal(false); enrollForm.resetFields(); fetchEnrollments(); }
          else {
            // Try training enrollment
            const enr = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/training/enrollments.php?tenant_id=${apiService.getTenantId()}`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ employee_id: values.employee_id, program_id: selectedProgram?.program_id, start_date: new Date().toISOString().split('T')[0], status: 'enrolled', tenant_id: apiService.getTenantId() })
            });
            const d = await enr.json();
            if (d.success) { feedbackSuccess('Employee enrolled', d.email_sent, d.email_sent ? 'Enrollment confirmation sent to employee' : 'Enrolled but email notification could not be sent'); setEnrollModal(false); enrollForm.resetFields(); fetchEnrollments(); }
            else message.error(d.error || d.message || 'Failed to enroll');
          }
        }}>
          <Form.Item name="employee_id" label="Select Employee" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Choose employee">
              {employees
                .filter((e: any) => !enrollments.some((en: any) => en.employee_id === e.id && en.program_id === selectedProgram?.program_id))
                .map((e: any) => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>)}
            </Select>
          </Form.Item>
          <p style={{ color: '#666', fontSize: 12, marginBottom: 8 }}>Only showing employees not yet enrolled in this programme.</p>
          <Space><Button type="primary" htmlType="submit">Enroll</Button><Button onClick={() => setEnrollModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Kirkpatrick Evaluation Modal */}
      {kirkpatrickVisible && selectedProgram && (
        <Modal
          title={null}
          open={kirkpatrickVisible}
          onCancel={() => { setKirkpatrickVisible(false); setSelectedProgram(null); }}
          footer={null}
          width={1000}
          style={{ top: 20 }}
        >
          <KirkpatrickEvaluation
            programId={selectedProgram.program_id}
            programTitle={selectedProgram.title}
            enrollments={enrollments.filter((e: any) => e.program_id === selectedProgram.program_id).length > 0 ? enrollments.filter((e: any) => e.program_id === selectedProgram.program_id) : employees.map((e: any) => ({ ...e, employee_id: e.id, employee_name: `${e.first_name} ${e.last_name}`, enrollment_id: null, status: 'active' }))}
          />
        </Modal>
      )}

    </div>
  );
};


export default TrainingDevelopment;
